import { Component, Inject } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ParsedPhoneNumber, parsePhoneNumber, getExample } from 'awesome-phonenumber';
import { ISO_3166_1_CODES } from '../country-codes';
import { environment } from '../../../environments/environment';
import { ConnectorService, LogInBackDto } from '../../connector.service';
import { NotificationsService } from '../../notifications.service';
import { Account } from '../account.dto';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogOverviewExampleDialog } from '../account.component';

export const phoneValidator: ValidatorFn | ValidationErrors | null = (control: FormGroup): ValidationErrors | null => {
  const country = control.get('country');
  const num = control.get('number');
  if (num?.value && country?.value && !(parsePhoneNumber(num.value, { regionCode: country.value }).valid)) {
    return { invalidPhone: true };
  }
  return null;
};

export class SheetPhoneErrorMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    if (control === null) return true;
    return !!(control.value && control.touched && !control?.parent?.valid);
  }
}

@Component({
  selector: 'app-account-identity-sheet',
  templateUrl: './account-identity-sheet.component.html',
  styleUrls: ['./account-identity-sheet.component.scss']
})
export class AccountIdentitySheetComponent {
  account: Account;
  countyCodes = ISO_3166_1_CODES;
  editingPhone = false;
  usernameValidation: Boolean | null = null;

  profileForm = this.fb.group({
    phone: this.fb.group({
      country: ['FR'],
      number: ['']
    }, { validators: phoneValidator })
  });
  phoneErrorMatcher = new SheetPhoneErrorMatcher();

  constructor(
    private bottomSheetRef: MatBottomSheetRef<AccountIdentitySheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: { account: Account, onRefresh: () => void },
    private fb: FormBuilder,
    private http: HttpClient,
    private connector: ConnectorService,
    public notificationsService: NotificationsService,
    private dialog: MatDialog,
  ) {
    this.account = data.account;
  }

  close() { this.bottomSheetRef.dismiss(); }

  // --- Phone ---
  get phoneCountryControl() { return this.profileForm.get('phone.country') as FormControl; }
  get phoneNumberControl() { return this.profileForm.get('phone.number') as FormControl; }
  get phoneNumberDigits(): string { return this.phoneNumberControl.value.replace(/\D/g, ''); }
  get phoneNumber(): ParsedPhoneNumber { return parsePhoneNumber(this.phoneNumberDigits, { regionCode: this.phoneCountryControl.value }); }
  get phoneGroup() { return this.profileForm.get('phone') as FormControl; }
  get phoneHint(): string {
    const result = getExample(this.phoneCountryControl.value).number?.national;
    return result ?? '';
  }

  formatNumber() {
    const natNum = this.phoneNumber.number?.national;
    this.phoneNumberControl.setValue(natNum ? natNum : this.phoneNumberDigits);
  }

  isPhoneSet(): boolean {
    return this.account.phone !== null && this.account.phone.valid === true;
  }

  isPasswordChanged(): boolean {
    if (this.notificationsService.notifications.compteIssues === 2) return false;
    if (this.notificationsService.notifications.compteIssues === 1 && this.isPhoneSet()) return false;
    return true;
  }

  startEditPhone() { this.editingPhone = true; }
  cancelEditPhone() { this.editingPhone = false; }

  async validatePhoneNumber() {
    if (this.phoneNumber.valid) {
      this.http.post<Account>(environment.backAPI + '/compte/phone', { phone: this.phoneNumber }, {
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + this.connector.token() }
      }).subscribe(
        (result) => {
          if (result !== undefined && !(result instanceof HttpErrorResponse)) {
            this.editingPhone = false;
            this.data.onRefresh();
          } else { this.connector.disconnection(); }
        },
        () => { this.connector.disconnection(); }
      );
    } else {
      alert('Le format du numéro de téléphone est erroné');
    }
  }

  // --- Username ---
  setUsername(username: string) {
    this.account.username = username.replace(/[^a-zA-Z0-9_]/g, '');
  }

  async saveUsername() {
    this.http.post<Boolean>(environment.backAPI + '/compte/username', { username: this.account.username }, {
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + this.connector.token() }
    }).subscribe(
      (result) => {
        if (result !== undefined && !(result instanceof HttpErrorResponse)) {
          this.usernameValidation = result;
          setTimeout(() => { this.usernameValidation = null; }, 2000);
          if (!result) alert('Mise à jour du pseudo non prise en compte');
        } else { this.connector.disconnection(); }
      },
      () => { this.connector.disconnection(); }
    );
  }

  // --- Password ---
  openPasswordDialog() {
    const dialogRef = this.dialog.open(DialogOverviewExampleDialog, {
      data: { title: 'Modification du mot de passe', password: '', display: true },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.http.post<LogInBackDto>(environment.backAPI + '/compte/password', { password: result }, {
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + this.connector.token() }
        }).subscribe(
          (res) => {
            if (res !== undefined && !(res instanceof HttpErrorResponse)) {
              this.connector.initSession(res);
              alert('Votre mot de passe a bien été modifié');
              this.data.onRefresh();
            } else { this.connector.disconnection(); }
          },
          () => { this.connector.disconnection(); }
        );
      }
    });
  }
}
